import React, { useContext, useEffect, useRef, useState } from "react"
import Col from "react-bootstrap/Col"
import Row from "react-bootstrap/Row"

import Image from "@atoms/Image/Image"
import InlineLinkListItem from "@atoms/InlineLinkListItem/InlineLinkListItem"
import { IntersectionContext } from "@atoms/IntersectionObserver/IntersectionObserver"
import InlineLinkList from "@molecules/InlineLinkList/InlineLinkList"

import styles from "./Combo.module.scss"

const Combo = ({
  title,
  text,
  image,
  videoUrl,
  quoted,
  links,
  aspectRatio,
  variant,
}) => {
  const linksFormatted = Array.isArray(links)
    ? links
        .map((item, index) => {
          return item.link ? (
            <InlineLinkListItem
              key={index + item.link.title}
              href={item.link.url}
              text={item.link.title}
              internal={item.link.target !== "_blank"}
            />
          ) : null
        })
        .filter(Boolean)
    : []

  const showImage = image && !videoUrl
  const showVideo = videoUrl
  const showHighLightStyles = variant === "highlight" && !showVideo

  const video = useRef()
  const { inView } = useContext(IntersectionContext)
  const [playInProgress, setPlayInProgress] = useState(false)

  useEffect(() => {
    if (video) {
      if (inView) {
        // Using the promise to make sure the video is actually playing
        // before it can be paused. Trying to pause a video which is not playing
        // leads to Big Problems
        // https://developer.chrome.com/blog/play-request-was-interrupted/
        let playPromise = video.current?.play()

        if (playPromise !== undefined) {
          playPromise.then((_) => {
            setPlayInProgress(true)
          })
        }
      } else {
        if (playInProgress) {
          video.current?.pause()
          setPlayInProgress(false)
        }
      }
    }
  }, [video, inView, playInProgress])

  return (
    <div
      className={`${styles.element} ${
        showHighLightStyles ? styles.highlight : ""
      } make-full-width-on-mobile`}
    >
      <Row className="no-gutters mobile-bottom-margins tablet-bottom-margins">
        {showImage && (
          <Col lg={{ span: 6, order: variant === "highlight" ? 2 : 1 }}>
            <div className={styles.image}>
              <Image
                image={image}
                aspectRatio={aspectRatio}
                widths={[313, 612, 750, 1080]}
                fill={true}
              />
            </div>
          </Col>
        )}
        {showVideo && (
          <Col lg={{ span: 6, order: variant === "highlight" ? 2 : 1 }}>
            <div className={styles.video}>
              <video
                ref={video}
                loop
                playsInline
                muted
                tabIndex="-1"
                src={videoUrl}
              />
            </div>
          </Col>
        )}
        <Col
          lg={{ span: 6, order: variant === "highlight" ? 1 : 2 }}
          className="align-self-center"
        >
          <div
            className={`${styles.content} make-full-width-on-mobile-with-paddings`}
          >
            <h2>{title}</h2>
            <p>{text}</p>
            {quoted && <p>&ndash; {quoted}</p>}
            {linksFormatted && (
              <InlineLinkList>{linksFormatted}</InlineLinkList>
            )}
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default Combo

Combo.defaultProps = {
  variant: "regular",
}
